import React, { useCallback, useRef, useState } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TYPES } from "@utils/inversifyJS/types";
import type { DeleteRawFileService } from "@application/useCases/rawFiles/DeleteRawFileService";
import type { RawFileJobUsage } from "@domain/repositories/IRawFileRepository";
import { DeleteRawFileDialog } from "./DeleteRawFileDialog";

interface DeleteTarget {
  /**
  Full path of the item relative to the raw files location, e.g. `project-a/sample.raw`.
  */
  id: string;
  fileName: string;
  folder: string;
  isFolder: boolean;
}

interface UseDeleteRawFileDialogOptions {
  /**
  Invoked after a successful deletion so the caller can refresh listings and prune selection state.
  */
  onDeleted: (deleted: DeleteTarget) => void | Promise<void>;
}

/**
 * Owns the raw file deletion flow: usage lookup, confirmation, deletion and error surfacing.
 *
 * Extracted from FileExplorerGrid, which is already large, following the existing
 * `useArtifactDownloadDialog` / `useFailedBatchesDialog` convention.
 */
export const useDeleteRawFileDialog = ({ onDeleted }: UseDeleteRawFileDialogOptions) => {
  const deleteRawFileService = useInjection<DeleteRawFileService>(TYPES.DeleteRawFileService);

  const [target, setTarget] = useState<DeleteTarget | null>(null);
  const [usedByJobs, setUsedByJobs] = useState<RawFileJobUsage[] | undefined>(undefined);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [usageLookupFailed, setUsageLookupFailed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
  Identifies the dialog instance that owns the in-flight usage lookup.

  The lookup is asynchronous, so without this a request started for file A can resolve after the user has
  cancelled and opened file B, overwriting B's warning with A's result and enabling B's Delete button on the
  strength of a check that was never run against B. Opening or closing the dialog invalidates any pending
  request by advancing the counter; a resolved request only applies its outcome while it is still current.
  */
  const lookupGenerationRef = useRef(0);

  const closeDialog = useCallback(() => {
    lookupGenerationRef.current += 1;
    setTarget(null);
    setUsedByJobs(undefined);
    setIsLoadingUsage(false);
    setUsageLookupFailed(false);
    setIsDeleting(false);
  }, []);

  const openDeleteDialog = useCallback(
    async (nextTarget: DeleteTarget) => {
      const generation = ++lookupGenerationRef.current;

      setTarget(nextTarget);
      setUsedByJobs(undefined);
      setUsageLookupFailed(false);
      setIsDeleting(false);

      // Folders warn unconditionally, so there is nothing to look up. Checking them would mean a recursive
      // scan of every file beneath the prefix.
      if (nextTarget.isFolder) {
        setUsedByJobs([]);
        setIsLoadingUsage(false);
        return;
      }

      setIsLoadingUsage(true);

      try {
        const usage = await deleteRawFileService.getUsageAsync(nextTarget.fileName, nextTarget.folder || undefined);
        if (lookupGenerationRef.current !== generation) {
          return;
        }
        setUsedByJobs(usage.jobs ?? []);
      } catch (error) {
        // A failed lookup must not imply the file is unused, so the dialog says so explicitly.
        console.error("Failed to resolve raw file usage:", error);
        if (lookupGenerationRef.current !== generation) {
          return;
        }
        setUsageLookupFailed(true);
        setUsedByJobs([]);
      } finally {
        if (lookupGenerationRef.current === generation) {
          setIsLoadingUsage(false);
        }
      }
    },
    [deleteRawFileService],
  );

  const confirmDelete = useCallback(async () => {
    if (!target) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteRawFileService.executeAsync(target.fileName, target.folder || undefined, target.isFolder);
      await onDeleted(target);
      closeDialog();
    } catch (error) {
      console.error("Failed to delete raw file:", error);
      setIsDeleting(false);
      alert(`Failed to delete ${target.fileName}.`);
    }
  }, [closeDialog, deleteRawFileService, onDeleted, target]);

  const deleteRawFileDialog = target ? (
    <DeleteRawFileDialog
      fileName={target.fileName}
      folder={target.folder}
      isDirectoryBundle={target.fileName.toLowerCase().endsWith(".d")}
      isFolder={target.isFolder}
      usedByJobs={usedByJobs}
      isLoadingUsage={isLoadingUsage}
      isDeleting={isDeleting}
      usageLookupFailed={usageLookupFailed}
      onConfirm={() => void confirmDelete()}
      onCancel={closeDialog}
    />
  ) : null;

  return { deleteRawFileDialog, openDeleteDialog };
};

export type { DeleteTarget };
